import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Project templates manager. Lives behind a modal-backdrop in
// Projects.tsx so the sidebar stays uncluttered; the user opens it via
// the "Templates" button, picks an existing template to spin up a new
// project (with a custom project title), or saves a new template for
// reuse later. Tasks captured on a template become the linear roadmap
// chain on every project instantiated from it.
import { notify } from "./Toast";
import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
export default function ProjectTemplatesModal({ canManage, currentUserId, isAdmin, units = [], onClose, onInstantiated, }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNew, setShowNew] = useState(false);
    // Which template is currently selected for the "Instantiate" panel —
    // null when nothing is selected and the user is browsing the list.
    const [pickedId, setPickedId] = useState(null);
    const [newProjectName, setNewProjectName] = useState("");
    // Empty string means "use the template's BU"; explicit values override.
    const [pickedUnitId, setPickedUnitId] = useState("");
    const [busy, setBusy] = useState(false);
    async function load() {
        setLoading(true);
        setError(null);
        try {
            const rows = await api.get("/project-templates");
            setTemplates(rows);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load templates");
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);
    async function instantiate() {
        if (!pickedId || !newProjectName.trim())
            return;
        setBusy(true);
        try {
            const out = await api.post(`/project-templates/${pickedId}/instantiate`, {
                name: newProjectName.trim(),
                // Caller can override the template's stored BU — keep null
                // (empty string) to inherit, send an explicit value to retag
                // the spawned project. Backend honors whichever is sent.
                business_unit_id: pickedUnitId || null,
            });
            onInstantiated(out.project_id);
            onClose();
        }
        catch (e) {
            if (e instanceof ApiError) {
                notify.error(typeof e.body === "object" && e.body && "detail" in e.body
                    ? String(e.body.detail)
                    : e.message);
            }
            else {
                notify.error(e instanceof Error ? e.message : "Couldn't create project");
            }
        }
        finally {
            setBusy(false);
        }
    }
    async function deleteTemplate(id, name) {
        if (!confirm(`Delete the "${name}" template? This can't be undone.`))
            return;
        try {
            await api.del(`/project-templates/${id}`);
            setTemplates((cur) => cur.filter((t) => t.id !== id));
            if (pickedId === id)
                setPickedId(null);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete template");
        }
    }
    const picked = pickedId ? templates.find((t) => t.id === pickedId) : null;
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal templates-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "modal-head", children: [_jsx("h3", { children: "Project templates" }), _jsx("button", { className: "btn-link", onClick: onClose, children: "\u2715" })] }), _jsx("p", { className: "muted small", children: "Save the shape of projects you run repeatedly \u2014 name it once, list the tasks once. Spinning up the next engagement becomes a single field (the new project title)." }), loading && _jsx("div", { className: "muted", children: "Loading templates\u2026" }), error && _jsx("div", { className: "error", children: error }), !loading && !error && (_jsxs("div", { className: "templates-modal-body", children: [_jsxs("div", { className: "templates-list", children: [_jsxs("div", { className: "templates-list-head", children: [_jsx("strong", { children: "Saved templates" }), canManage && (_jsx("button", { className: "btn-link", onClick: () => setShowNew(true), type: "button", children: "+ New template" }))] }), templates.length === 0 && (_jsxs("div", { className: "muted small", children: ["No templates yet.", canManage && " Click + New template to save one."] })), _jsx("ul", { children: templates.map((t) => {
                                        const canEdit = isAdmin || t.owner_id === currentUserId;
                                        return (_jsxs("li", { className: `template-row ${pickedId === t.id ? "active" : ""}`, onClick: () => {
                                                setPickedId(t.id);
                                                setNewProjectName("");
                                            }, children: [_jsxs("div", { className: "template-row-main", children: [_jsx("div", { className: "template-row-title", children: t.name }), _jsxs("div", { className: "muted small", children: [t.tasks.length, " task", t.tasks.length === 1 ? "" : "s"] })] }), canEdit && (_jsx("button", { type: "button", className: "btn-link template-row-del", onClick: (e) => {
                                                        e.stopPropagation();
                                                        deleteTemplate(t.id, t.name);
                                                    }, title: "Delete this template", children: "Delete" }))] }, t.id));
                                    }) })] }), _jsx("div", { className: "template-detail", children: showNew && canManage ? (_jsx(NewTemplateForm, { onCreated: async (created) => {
                                    setTemplates((cur) => [created, ...cur]);
                                    setShowNew(false);
                                    setPickedId(created.id);
                                }, onCancel: () => setShowNew(false) })) : picked ? (_jsxs(_Fragment, { children: [_jsx("h4", { children: picked.name }), picked.description && (_jsx("p", { className: "muted small", children: picked.description })), _jsx("div", { className: "muted small", children: "Tasks in order:" }), _jsx("ol", { className: "template-task-list", children: [...picked.tasks]
                                            .sort((a, b) => a.order_index - b.order_index)
                                            .map((task) => (_jsx("li", { children: task.title }, task.id))) }), canManage && (_jsxs("div", { className: "template-instantiate", children: [_jsxs("label", { className: "project-type-label", children: ["New project title", _jsx("input", { type: "text", autoFocus: true, placeholder: "e.g. Sound Ark", value: newProjectName, onChange: (e) => setNewProjectName(e.target.value) })] }), _jsxs("label", { className: "project-type-label", children: ["Business unit", _jsxs("select", { value: pickedUnitId, onChange: (e) => setPickedUnitId(e.target.value), children: [_jsx("option", { value: "", children: picked.business_unit_id
                                                                    ? "— same as template —"
                                                                    : "— none —" }), units.map((u) => (_jsx("option", { value: u.id, children: u.name }, u.id)))] })] }), _jsx("button", { type: "button", className: "btn-primary", disabled: !newProjectName.trim() || busy, onClick: instantiate, children: busy ? "Creating…" : "Create project from template" })] }))] })) : (_jsx("div", { className: "muted", children: "Select a template on the left to spin up a project from it." })) })] }))] }) }));
}
// ----- New template form ----------------------------------------------------
function NewTemplateForm({ onCreated, onCancel, }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    // The user types one task per line in a textarea. Easier than juggling
    // an array of inputs for the common "I just want these 3 steps" case.
    const [tasksRaw, setTasksRaw] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit() {
        if (!name.trim())
            return;
        const tasks = tasksRaw
            .split("\n")
            .map((t) => t.trim())
            .filter(Boolean)
            .map((title, i) => ({ title, order_index: i }));
        setBusy(true);
        try {
            const created = await api.post("/project-templates", {
                name: name.trim(),
                description: description.trim() || null,
                tasks,
            });
            onCreated(created);
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't create template");
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("form", { onSubmit: (e) => {
            e.preventDefault();
            submit();
        }, children: [_jsx("h4", { children: "New template" }), _jsxs("label", { className: "project-type-label", children: ["Template name", _jsx("input", { type: "text", autoFocus: true, value: name, placeholder: "e.g. Sales Acquisition", onChange: (e) => setName(e.target.value) })] }), _jsxs("label", { className: "project-type-label", children: ["Description (optional)", _jsx("input", { type: "text", value: description, placeholder: "When and how this process gets used", onChange: (e) => setDescription(e.target.value) })] }), _jsxs("label", { className: "project-type-label", children: ["Tasks (one per line, in order)", _jsx("textarea", { rows: 6, value: tasksRaw, placeholder: "Discovery Call\nInternal Audit\nSend Findings & Next Steps", onChange: (e) => setTasksRaw(e.target.value) })] }), _jsxs("div", { className: "row", children: [_jsx("button", { type: "submit", className: "btn-primary", disabled: !name.trim() || busy, children: busy ? "Saving…" : "Save template" }), _jsx("button", { type: "button", onClick: onCancel, children: "Cancel" })] })] }));
}
